import { HabitSchedule, ScheduleEntryState, Timestamp } from '@griply/types';

import { getTimeframeCompletions } from './get-timeframe-completions';
import { getTimeframeDateEntries } from './get-timeframe-date-entries';
import { getTimeframeSchedules } from './schedules/get-timeframe-schedules';

export type GetTimeframeProgressOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
};

export type TimeframeProgress = {
  /** What the habit logged in the range. `undefined` when no schedule governs the range at all. */
  completionsCount: number | undefined;
  /** What it was supposed to log. `undefined` for the same reason. */
  target: number | undefined;
};

// The "x out of y" of a habit for any range; a frequency schedule has no per-day target to sum.
export const getTimeframeProgress = (
  schedules: HabitSchedule[],
  { startDate, endDate }: GetTimeframeProgressOptions,
): TimeframeProgress => {
  // Latest-starting, not last-stored: `>=` keeps the later array position on a tie, so engines agree.
  const lastSchedule = getTimeframeSchedules(schedules, {
    startDate,
    endDate,
  }).reduce<HabitSchedule | undefined>(
    (latest, schedule) =>
      !latest || schedule.startDate.getTime() >= latest.startDate.getTime()
        ? schedule
        : latest,
    undefined,
  );
  if (!lastSchedule) {
    return { completionsCount: undefined, target: undefined };
  }

  const { timeframeCompletions } = getTimeframeCompletions(schedules, {
    startDate,
    endDate,
  });

  if (lastSchedule.frequency) {
    return {
      completionsCount: timeframeCompletions.reduce(
        (acc, { state, value }) =>
          acc + (state === ScheduleEntryState.Complete && !!value ? value : 0),
        0,
      ),
      target: lastSchedule.frequency.count ?? 1,
    };
  }

  const { timeframeDateEntries } = getTimeframeDateEntries(schedules, {
    startDate,
    endDate,
  });

  return {
    completionsCount: timeframeCompletions.reduce(
      (acc, { value }) => acc + (value ?? 0),
      0,
    ),
    target: timeframeDateEntries.reduce(
      (acc, entry) => acc + (entry.target ?? 0),
      0,
    ),
  };
};
